// Point of Sale (/pos) — the counter app for selling drinks, snacks and gear
// alongside court bookings.
//
// Its constants live HERE rather than in bookingConst.js on purpose. The two
// share a Firebase project and a palette and nothing else: no POS value is
// read by the booking app, and no booking value is read here. Folding them
// together would grow an already 26KB file with a second product's
// vocabulary and make it harder to see what the booking app actually
// depends on.

// ---- Firebase nodes ----
// Prefixed `pos` so the module's data is obvious at a glance in the console
// and can be secured as one subtree in database.rules.json.
export const POS_CATEGORIES_NODE = 'posCategories';
export const POS_PRODUCTS_NODE = 'posProducts';
export const POS_ORDERS_NODE = 'posOrders';
export const POS_AUDIT_NODE = 'posAuditLog';

// ---- Audit ----
// A SEPARATE trail from the booking app's /auditLog. A counter sale and a
// court reservation are different businesses to review, and mixing them would
// bury one in the other — the booking trail is already the busier node.
export const POS_AUDIT_ACTIONS = {
  PRODUCT_CREATED: 'pos_product_created',
  PRODUCT_UPDATED: 'pos_product_updated',
  PRODUCT_DELETED: 'pos_product_deleted',
  CATEGORY_ADDED: 'pos_category_added',
  CATEGORY_REMOVED: 'pos_category_removed',
  ORDER_PLACED: 'pos_order_placed',
};

export const POS_AUDIT_LABELS = {
  [POS_AUDIT_ACTIONS.PRODUCT_CREATED]: 'Product registered',
  [POS_AUDIT_ACTIONS.PRODUCT_UPDATED]: 'Product updated',
  [POS_AUDIT_ACTIONS.PRODUCT_DELETED]: 'Product removed',
  [POS_AUDIT_ACTIONS.CATEGORY_ADDED]: 'Category added',
  [POS_AUDIT_ACTIONS.CATEGORY_REMOVED]: 'Category removed',
  [POS_AUDIT_ACTIONS.ORDER_PLACED]: 'Order placed',
};

// How loudly each action reads on the trail. Selling and restocking are
// routine; removing a product or a category destroys something.
export const POS_AUDIT_TONES = {
  [POS_AUDIT_ACTIONS.PRODUCT_CREATED]: 'info',
  [POS_AUDIT_ACTIONS.PRODUCT_UPDATED]: 'warning',
  [POS_AUDIT_ACTIONS.PRODUCT_DELETED]: 'danger',
  [POS_AUDIT_ACTIONS.CATEGORY_ADDED]: 'info',
  [POS_AUDIT_ACTIONS.CATEGORY_REMOVED]: 'danger',
  [POS_AUDIT_ACTIONS.ORDER_PLACED]: 'success',
};

// ---- Screens ----
export const POS_TABS = [
  { key: 'dashboard', label: 'Products' },
  { key: 'sales', label: 'Sales' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'audit', label: 'Audit Trail' },
];

/**
 * Rows per page in every paged POS list — the sales preview and the audit
 * trail. Named here rather than per-screen so the two cannot disagree about
 * what a page is; PosPager reads it as its default.
 */
export const POS_PAGE_SIZE = 10;

/** The one thing staff must not get wrong, so it is stated on the receipt. */
export const POS_PAYMENT_NOTE = 'Only tap Confirm once the customer has paid.';

/** How many products the Sales bar chart ranks. */
export const POS_TOP_PRODUCTS_LIMIT = 10;

/** Newest orders streamed into the Sales screen; older ones stay queryable. */
export const POS_RECENT_ORDERS_LIMIT = 300;

// Search runs from the first character typed (the brief), so this is 1 rather
// than the usual 2-3 — named here so the Inventory screen and any future
// caller cannot disagree about it.
export const POS_SEARCH_MIN_CHARS = 1;

/** The "all categories" tab. Not a real category, so it cannot collide. */
export const POS_ALL_CATEGORIES = '__all__';
